import React, { useMemo, useState } from 'react'
import { Box, Typography, List, Stack, Divider, TextField, InputAdornment } from '@mui/material'
import { Search } from '@mui/icons-material'
import NavigationItem from './NavigationItem'
import { MAIN_SEGMENT_KEYS, MAIN_SEGMENT_TITLES } from '../constants'
import { SelectedItem } from './InspectorLayout'
import useDebouncedValue from 'hooks/useDebouncedValue'

export interface NavigationPaneProps {
  data: any
  selectedItem: SelectedItem | null
  expandedItems: Set<string>
  onItemSelect: (item: SelectedItem) => void
  onItemExpand: (itemId: string) => void
  onItemCollapse: (itemId: string) => void
  readonly?: boolean
}

const NavigationPane = React.memo<NavigationPaneProps>(
  ({
    data,
    selectedItem,
    expandedItems,
    onItemSelect,
    onItemExpand,
    onItemCollapse,
    readonly = false,
  }) => {
    const [searchTerm, setSearchTerm] = useState('')
    const debouncedSearchTerm = useDebouncedValue(searchTerm, 300) // 300ms delay for better responsiveness

    // Recursive function to check if an item or its children match the search term
    const itemMatchesSearch = (item: any, searchTerm: string): boolean => {
      if (!searchTerm.trim()) return true

      const searchLower = searchTerm.toLowerCase()

      // Check if current item matches
      const titleMatch = item.title?.toLowerCase().includes(searchLower)
      const idMatch = item.data?.id?.toLowerCase().includes(searchLower)
      const descriptionMatch = item.data?.description?.toLowerCase().includes(searchLower)

      if (titleMatch || idMatch || descriptionMatch) {
        return true
      }

      // Check children recursively
      if (item.children && item.children.length > 0) {
        return item.children.some((child: any) => itemMatchesSearch(child, searchTerm))
      }

      return false
    }

    // Recursive function to filter items based on search term
    const filterItemsBySearch = (items: any[], searchTerm: string): any[] => {
      if (!searchTerm.trim()) return items

      return items
        .map(item => {
          // If item has children, recursively filter them first
          let filteredChildren: any[] = []
          if (item.children && item.children.length > 0) {
            filteredChildren = filterItemsBySearch(item.children, searchTerm)
          }

          // Check if current item matches
          const currentItemMatches = itemMatchesSearch(item, searchTerm)

          // If current item matches or has matching children, include it
          if (currentItemMatches || filteredChildren.length > 0) {
            return {
              ...item,
              children: filteredChildren,
            }
          }

          return null
        })
        .filter(Boolean) // Remove null items
    }

    // Recursive function to build navigation items for loops and their children
    const buildLoopItems = (loops: any[], parentPath: string[] = []): any[] => {
      return loops.map((loop: any, index: number) => {
        const currentPath = [...parentPath, index.toString()]
        const loopId = `loop-${parentPath.join('-')}-${loop.id || index}`

        // Check if this loop has nested loops
        const hasNestedLoops = loop.loops && loop.loops.length > 0
        const hasSegments = loop.segments && loop.segments.length > 0

        const children: any[] = []

        if (hasSegments) {
          // Add segments as children first
          children.push(
            ...loop.segments.map((segment: any, segIndex: number) => ({
              id: `segment-${parentPath.join('-')}-${index}-${segment.id || segIndex}`,
              type: 'segment',
              title: segment.name || `Segment ${segIndex + 1}`,
              data: segment,
              path: [...currentPath, 'segments', segIndex.toString()],
              children:
                segment.elements?.map((element: any, elemIndex: number) => ({
                  id: `element-${parentPath.join('-')}-${index}-${segIndex}-${
                    element.id || elemIndex
                  }`,
                  type: 'element',
                  title: element.name || `Element ${elemIndex + 1}`,
                  data: element,
                  path: [
                    ...currentPath,
                    'segments',
                    segIndex.toString(),
                    'elements',
                    elemIndex.toString(),
                  ],
                })) || [],
            }))
          )
        }

        if (hasNestedLoops) {
          // Add nested loops as children last
          children.push(...buildLoopItems(loop.loops, [...currentPath, 'loops']))
        }

        return {
          id: loopId,
          type: 'loop',
          title: loop.name || `Loop ${index + 1}`,
          data: loop,
          path: currentPath,
          children,
        }
      })
    }

    // Generate navigation items from data
    const navigationItems = useMemo(() => {
      const items: any[] = []

      // Add main segments
      MAIN_SEGMENT_KEYS.forEach(segmentKey => {
        if (segmentKey === 'loops') {
          // Handle loops specially with recursive structure
          const loops = data.loops || []
          items.push({
            id: 'loops',
            type: 'main-segment',
            title: MAIN_SEGMENT_TITLES[segmentKey],
            data: { loops },
            children: buildLoopItems(loops, ['loops']),
          })
        } else {
          // Handle other main segments
          const segment = data[segmentKey]
          if (segment) {
            const hasElements = segment.elements && segment.elements.length > 0

            items.push({
              id: segmentKey,
              type: 'main-segment',
              title: MAIN_SEGMENT_TITLES[segmentKey],
              data: segment,
              path: [segmentKey],
              children: hasElements
                ? segment.elements.map((element: any, elemIndex: number) => ({
                    id: `element-${segmentKey}-${element.id || elemIndex}`,
                    type: 'element',
                    title: element.name || element.description || `Element ${elemIndex + 1}`,
                    data: element,
                    path: [segmentKey, 'elements', elemIndex.toString()],
                  }))
                : [],
            })
          }
        }
      })

      return items
    }, [data])

    // Filter navigation items based on search term
    const filteredNavigationItems = useMemo(() => {
      return filterItemsBySearch(navigationItems, debouncedSearchTerm)
    }, [navigationItems, debouncedSearchTerm])

    // Auto-expand items that have matching children when searching, or collapse all when search is cleared
    React.useEffect(() => {
      if (debouncedSearchTerm.trim()) {
        const expandMatchingParents = (items: any[]) => {
          items.forEach(item => {
            if (item.children && item.children.length > 0) {
              // Check if any children match the search
              const hasMatchingChildren = item.children.some((child: any) =>
                itemMatchesSearch(child, debouncedSearchTerm)
              )

              if (hasMatchingChildren && !expandedItems.has(item.id)) {
                onItemExpand(item.id)
              }

              // Recursively check children
              expandMatchingParents(item.children)
            }
          })
        }

        expandMatchingParents(navigationItems)
      } else {
        // When search is cleared, collapse all items
        const collapseAllItems = (items: any[]) => {
          items.forEach(item => {
            if (item.children && item.children.length > 0) {
              if (expandedItems.has(item.id)) {
                onItemCollapse(item.id)
              }
              // Recursively collapse children
              collapseAllItems(item.children)
            }
          })
        }

        collapseAllItems(navigationItems)
      }
    }, [debouncedSearchTerm, navigationItems, onItemExpand, onItemCollapse])

    const handleItemSelect = (item: any) => {
      const selectedItemData: SelectedItem = {
        type: item.type,
        id: item.id,
        path: item.path || [],
        data: item.data,
      }
      onItemSelect(selectedItemData)
    }

    const handleItemExpand = (itemId: string) => {
      onItemExpand(itemId)
    }

    const handleItemCollapse = (itemId: string) => {
      onItemCollapse(itemId)
    }

    return (
      <Box
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Stack spacing={1} padding={2} sx={{ flexShrink: 0 }}>
          <Typography variant="h6" component="h2" fontWeight={'bold'}>
            Navigation
          </Typography>

          {/* Search Input */}
          <TextField
            placeholder="Search by ID or description..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '0.875rem',
              },
            }}
          />

          <Divider />
        </Stack>

        {/* Navigation List with scroll overflow */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            minHeight: 0, // Important for flex child to shrink
          }}
        >
          {filteredNavigationItems.length > 0 ? (
            <List dense sx={{ padding: 2, paddingTop: 0 }}>
              {filteredNavigationItems.map((item, index) => (
                <NavigationItem
                  key={item.id}
                  item={item}
                  level={0}
                  selectedItem={selectedItem}
                  expandedItems={expandedItems}
                  onSelect={handleItemSelect}
                  onExpand={handleItemExpand}
                  onCollapse={handleItemCollapse}
                  readonly={readonly}
                  isLastChild={index === filteredNavigationItems.length - 1}
                  searchTerm={debouncedSearchTerm.trim()}
                />
              ))}
            </List>
          ) : debouncedSearchTerm.trim() ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                padding: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No items found matching &quot;{debouncedSearchTerm}&quot;
              </Typography>
            </Box>
          ) : (
            <List dense sx={{ padding: 2, paddingTop: 0 }}>
              {navigationItems.map((item, index) => (
                <NavigationItem
                  key={item.id}
                  item={item}
                  level={0}
                  selectedItem={selectedItem}
                  expandedItems={expandedItems}
                  onSelect={handleItemSelect}
                  onExpand={handleItemExpand}
                  onCollapse={handleItemCollapse}
                  readonly={readonly}
                  isLastChild={index === navigationItems.length - 1}
                  searchTerm={debouncedSearchTerm.trim()}
                />
              ))}
            </List>
          )}
        </Box>
      </Box>
    )
  }
)

NavigationPane.displayName = 'NavigationPane'

export default NavigationPane
